import { defineStore } from 'pinia';
import { useAdvisorStore } from './advisorStore.js';

export const useTeamsStore = defineStore('teams', {
  state: () => ({
    hasFetched: false,
    hasFetchedSubjects: false,
    isFetching: false,
    isFetchingPermissions: false,
    isSavingTeam: false,
    isUpdatingMember: [],
    items: [],
    subjects: [],
  }),

  actions: {
    // Utilities

    // Ensure teams always have a members array for Vue 2 reactivity
    normalizeTeam(team, keepExisting = false, existing = null) {
      if (!team) {
        return null;
      }

      // Clone all fields
      const normalized = { ...team };

      if (Object.prototype.hasOwnProperty.call(team, 'members')) {
        // Only overwrite if the API actually included "members"
        normalized.members = Array.isArray(team.members) ? team.members : [];
      } else if (keepExisting && existing?.members) {
        // Fall back to existing team’s members if available
        normalized.members = existing.members;
      } else {
        // Default empty
        normalized.members = [];
      }

      return normalized;
    },

    normalizeArray(v) {
      return Array.isArray(v) ? v : [];
    },

    // Update or insert team
    upsertTeam(team) {
      if (!team || !team.external_id) {
        return;
      }

      const idx = this.items.findIndex(t => t.external_id === team.external_id);
      const existing = idx !== -1 ? this.items[idx] : null;
      const normalizedTeam = this.normalizeTeam(team, true, existing);

      if (idx === -1) {
        this.items.push(normalizedTeam);
      } else {
        // Vue 2 reactivity
        this.items.splice(idx, 1, { ...existing, ...normalizedTeam });
      }
    },

    setFetching(newValue) {
      this.isFetching = newValue;
    },

    setSavingTeam(newValue) {
      this.isSavingTeam = newValue;
    },

    setUpdatingMember(memberExternalId, adding = true) {
      if (adding) {
        if (!this.isUpdatingMember.includes(memberExternalId)) {
          this.isUpdatingMember.push(memberExternalId);
        }
      } else {
        const idx = this.isUpdatingMember.indexOf(memberExternalId);

        if (idx !== -1) {
          this.isUpdatingMember.splice(idx, 1); // Vue 2 reactivity
        }
      }
    },

    setTeams(teams) {
      const normalized = Array.isArray(teams) ?
        teams.map(this.normalizeTeam) :
        [];
      this.items = normalized;
      this.hasFetched = true;
    },

    setTeamField(teamExternalId, field, value) {
      // allowlist to avoid accidental overwrites
      const allowed = new Set(['members', 'permissions']);

      if (!allowed.has(field)) {
        console.warn(`setTeamField: '${field}' is not allowed`);
        return;
      }

      const safe = this.normalizeArray(value);
      const idx = this.items.findIndex(t => t.external_id === teamExternalId);

      if (idx !== -1) {
        const updated = { ...this.items[idx], [field]: safe };
        this.items.splice(idx, 1, updated); // keep reactive
        return;
      }

      this.fetchSingleTeam(teamExternalId)
          .then(fetched => {
            if (fetched) {
              this.upsertTeam({ ...fetched, [field]: safe });
            } else {
              this.upsertTeam({ external_id: teamExternalId, doesNotExist: true, [field]: safe });
            }
          })
          .catch(err => console.error('Error upserting team after fetch:', err));
    },

    // Network actions
    async fetchSubjects() {
      if (this.hasFetchedSubjects) {
        return;
      }

      this.setFetching(true);

      try {
        const response = await fetch(`/api/v1/teams/subjects/`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        this.subjects = data;
      } catch (error) {
        console.error('Error fetching team subjects:', error);
      } finally {
        this.setFetching(false);
        this.hasFetchedSubjects = true;
      }
    },

    async fetchTeams() {
      this.setFetching(true);

      try {
        const response = await fetch(`/api/v1/teams/`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        this.setTeams(data);
      } catch (error) {
        console.error('Error fetching teams:', error);
        this.setTeams([]);
      } finally {
        this.setFetching(false);
      }
    },

    async fetchTeamMembers(teamExternalId) {
      this.setFetching(true);

      try {
        const response = await fetch(
            `/api/v1/teams/${teamExternalId}/members/`,
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        this.setTeamField(teamExternalId, 'members', data);
      } catch (error) {
        console.error(`Error fetching ${teamExternalId} team members:`, error);
        this.setTeamField(teamExternalId, 'members', []);
      } finally {
        this.setFetching(false);
      }
    },

    async fetchTeamPermissions(teamExternalId) {
      this.isFetchingPermissions = true;

      try {
        const response = await fetch(
            `/api/v1/teams/${teamExternalId}/permissions/`,
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        this.setTeamField(teamExternalId, 'permissions', data);
      } catch (error) {
        console.error(`Error fetching ${teamExternalId} team permissions:`, error);
        this.setTeamField(teamExternalId, 'permissions', []);
      } finally {
        this.isFetchingPermissions = false;
      }
    },

    async fetchSingleTeam(teamExternalId) {
      try {
        const response = await fetch(`/api/v1/teams/${teamExternalId}/`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        this.upsertTeam(data);
        return data;
      } catch (error) {
        console.error(`Error fetching ${teamExternalId} team:`, error);
        return null;
      }
    },

    // This is a helper that will fetch a team, its members, and its permissions in one go.
    // It will first check if the team has already been fetched,
    // and will get members and permissions afterwards.
    async loadSingleTeam(teamExternalId) {
      this.setFetching(true);

      // Ensure team exists in store (fetch it if not)
      if (!this.items.some(t => t.external_id === teamExternalId)) {
        await Promise.all([
          this.fetchSingleTeam(teamExternalId),
          this.fetchTeamMembers(teamExternalId),
          this.fetchTeamPermissions(teamExternalId),
        ]);
        this.setFetching(false);
        return;
      }

      // Otherwise, just fetch members and permissions
      await Promise.all([
        this.fetchTeamMembers(teamExternalId),
        this.fetchTeamPermissions(teamExternalId),
      ]);
      this.setFetching(false);
    },

    // Members
    async fetchSingleTeamMember(teamExternalId, memberExternalId) {
      try {
        const res = await fetch(
            `/api/v1/teams/${teamExternalId}/members/${memberExternalId}/`,
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const member = await res.json();

        // Update in store
        const teamIdx = this.items.findIndex(
            t => t.external_id === teamExternalId,
        );
        if (teamIdx !== -1) {
          const team = this.items[teamIdx];
          const members = Array.isArray(team.members) ? [...team.members] : [];
          const mIdx = members.findIndex(
              m => m.external_id === memberExternalId,
          );

          if (mIdx !== -1) {
            members.splice(mIdx, 1, member); // replace existing member
          } else {
            members.push(member); // or add if not there
          }

          const updatedTeam = { ...team, members };
          this.items.splice(teamIdx, 1, updatedTeam); // Vue 2 reactivity
        }

        return member;
      } catch (e) {
        console.error(
            `Error fetching team member ${memberExternalId} in ${teamExternalId}:`,
            e,
        );
        return null;
      }
    },

    async updateSingleTeamMember(teamExternalId, memberExternalId, role) {
      if (!memberExternalId || this.isUpdatingMember.includes(memberExternalId)) {
        return;
      }

      const advisorStore = useAdvisorStore();
      this.setUpdatingMember(memberExternalId, true);

      try {
        const response = await fetch(
            `/api/v1/teams/${teamExternalId}/members/${memberExternalId}/`,
            {
              method: 'PATCH',
              body: JSON.stringify({
                roles: [role],
              }),
              headers: advisorStore.headers,
            },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        await this.fetchSingleTeamMember(teamExternalId, memberExternalId);
      } catch (error) {
        console.error(`Error fetching ${teamExternalId} team members:`, error);
      } finally {
        this.setUpdatingMember(memberExternalId, false);
      }
    },

    async removeTeamMember(teamExternalId, memberExternalId) {
      if (!teamExternalId || !memberExternalId || this.isUpdatingMember.includes(memberExternalId)) {
        return;
      }

      const advisorStore = useAdvisorStore();
      this.setUpdatingMember(memberExternalId, true);

      try {
        const response = await fetch(
            `/api/v1/teams/${teamExternalId}/members/${memberExternalId}/`,
            {
              method: 'DELETE',
              headers: advisorStore.headers,
            });

        if (!response.ok) {
          const errPayload = await response.json().catch(() => null);
          console.error('Delete failed payload:', errPayload);
          throw new Error(`HTTP ${response.status}`);
        }

        await this.fetchTeamMembers(teamExternalId);
      } catch (error) {
        console.error(`Error deleting team member:`, error);
        throw e;
      } finally {
        this.setUpdatingMember(memberExternalId, false);
      }
    },

    async addTeamMember(teamExternalId, payload) {
      if (!teamExternalId) {
        return;
      }

      const advisorStore = useAdvisorStore();
      this.setUpdatingMember(payload['advisor_external_id'], true);

      try {
        const url = `/api/v1/teams/${teamExternalId}/members/`;
        const method = 'POST';

        const response = await fetch(
            url,
            {
              method,
              body: JSON.stringify(payload),
              headers: advisorStore.headers,
            },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        await this.fetchTeamMembers(teamExternalId);
      } catch (error) {
        console.error(`Error adding member:`, error);
      } finally {
        this.setUpdatingMember(payload['advisor_external_id'], false);
      }
    },

    async updateTeamPermission(teamExternalId, actionId, subjectSlug, add = true) {
      const advisorStore = useAdvisorStore();
      this.setSavingTeam(true);

      try {
        const url = `/api/v1/teams/${teamExternalId}/permissions/${actionId}/${subjectSlug}/`;
        const method = add ? 'POST' : 'DELETE';

        const response = await fetch(
            url,
            {
              method,
              headers: advisorStore.headers,
            },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        this.fetchTeamPermissions(teamExternalId);
      } catch (error) {
        console.error(`Error updating team permission:`, error);
      } finally {
        this.setSavingTeam( false);
      }
    },

    async saveTeam(teamExternalId, name, description = '') {
      if (!name) {
        return;
      }

      const advisorStore = useAdvisorStore();
      this.setSavingTeam(true);

      try {
        const isEdit = Boolean(teamExternalId);
        const url = isEdit ?
          `/api/v1/teams/${teamExternalId}/` : // Trailing slash matters
          `/api/v1/teams/`;
        const method = isEdit ? 'PATCH' : 'POST';

        const response = await fetch(
            url,
            {
              method,
              body: JSON.stringify({
                name,
                description,
              }),
              headers: advisorStore.headers,
            },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        this.upsertTeam(data);
        return data;
      } catch (error) {
        console.error(`Error saving team:`, error);
      } finally {
        this.setSavingTeam( false);
      }
    },

    async deleteTeam(teamExternalId) {
      if (!teamExternalId) return false;

      const advisorStore = useAdvisorStore();
      this.setSavingTeam(true);

      try {
        const response = await fetch(`/api/v1/teams/${teamExternalId}/`, {
          method: 'DELETE',
          headers: advisorStore.headers,
        });

        if (!response.ok) {
          const errPayload = await response.json().catch(() => null);
          console.error('Delete failed payload:', errPayload);
          throw new Error(`HTTP ${response.status}`);
        }

        const idx = this.items.findIndex(t => t.external_id === teamExternalId);
        if (idx !== -1) this.items.splice(idx, 1); // Vue 2 reactivity-safe

        return true;
      } catch (error) {
        console.error(`Error deleting team:`, error);
        throw e;
      } finally {
        this.setSavingTeam(false);
      }
    },
  },
});
